import { TRUNCATED_SUFFIX } from './redact.js';
/** Longest `message` kept whole. Beyond this it is cut and marked. */
export const MAX_MESSAGE_LENGTH = 8192;
/** Longest `event` name accepted. Names are identifiers, not prose. */
export const MAX_EVENT_LENGTH = 200;
/** How far `error.cause` is followed before the chain is abandoned. */
export const MAX_CAUSE_DEPTH = 3;
/** Longest stack kept. A truncated stack still names the throwing frame. */
export const MAX_STACK_LENGTH = 8192;
export function truncate(text, max) {
    return text.length > max ? text.slice(0, max) + TRUNCATED_SUFFIX : text;
}
/**
 * Flatten an `Error` — or whatever was actually thrown, which is frequently not
 * an `Error` at all.
 *
 * A thrown string, a rejected promise carrying an object, `undefined` from a
 * library that called `reject()` with nothing: all of them arrive here, and a
 * logger that only handles real errors loses precisely the ones that are
 * hardest to reproduce.
 */
export function serializeError(value, depth = 0) {
    if (value instanceof Error) {
        const code = value.code;
        const cause = depth < MAX_CAUSE_DEPTH ? value.cause : undefined;
        return {
            name: value.name,
            message: truncate(value.message, MAX_MESSAGE_LENGTH),
            ...(value.stack ? { stack: truncate(value.stack, MAX_STACK_LENGTH) } : {}),
            ...(typeof code === 'string' || typeof code === 'number' ? { code: String(code) } : {}),
            ...(cause === undefined || cause === null
                ? {}
                : { cause: serializeError(cause, depth + 1) }),
        };
    }
    if (typeof value === 'string')
        return { name: 'Error', message: truncate(value, MAX_MESSAGE_LENGTH) };
    if (value === null || value === undefined) {
        return { name: 'Error', message: `Non-error thrown: ${String(value)}` };
    }
    // An object that merely looks like an error — a structured-clone survivor,
    // or something from another realm where `instanceof` does not hold.
    const shape = value;
    if (typeof shape.message === 'string') {
        return {
            name: typeof shape.name === 'string' ? shape.name : 'Error',
            message: truncate(shape.message, MAX_MESSAGE_LENGTH),
            ...(typeof shape.stack === 'string' ? { stack: truncate(shape.stack, MAX_STACK_LENGTH) } : {}),
        };
    }
    let described;
    try {
        described = JSON.stringify(value) ?? String(value);
    }
    catch {
        described = String(value);
    }
    return { name: 'Error', message: truncate(`Non-error thrown: ${described}`, MAX_MESSAGE_LENGTH) };
}
/**
 * Trim a user down to the four fields the record shape promises.
 *
 * Whatever the caller has — a JWT claim set, a database row — is very likely to
 * carry more, and passing it through whole is how a password hash ends up in a
 * log group.
 */
export function normalizeUser(value) {
    if (value === null || typeof value !== 'object')
        return undefined;
    const input = value;
    const id = firstString(input['id'], input['sub'], input['userId'], input['user_id']);
    const email = firstString(input['email']);
    // Without an id there is nothing stable to filter by, but an email alone is
    // still worth keeping — it is what a person searches for anyway.
    if (id === undefined && email === undefined)
        return undefined;
    const name = firstString(input['name'], input['displayName'], input['fullName']);
    const roles = Array.isArray(input['roles'])
        ? input['roles'].filter((role) => typeof role === 'string').slice(0, 20)
        : undefined;
    return {
        id: truncate(id ?? email ?? '', 200),
        ...(email === undefined ? {} : { email: truncate(email, 320) }),
        ...(name === undefined ? {} : { name: truncate(name, 200) }),
        ...(roles === undefined || roles.length === 0 ? {} : { roles }),
    };
}
function firstString(...candidates) {
    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim() !== '')
            return candidate.trim();
    }
    return undefined;
}
/**
 * Serialise a record to the one line a log group receives.
 *
 * Newlines inside the payload are the thing to watch: CloudWatch splits on
 * them, and half a JSON object is not a discoverable field — it is a line
 * Insights indexes as raw text, so the record silently stops being queryable.
 * `JSON.stringify` escapes them, which is exactly why the message is never
 * concatenated in by hand.
 */
export function recordToLine(record) {
    return JSON.stringify(record);
}
//# sourceMappingURL=record.js.map